import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

const ADMIN_EMAIL = 'bentleyesam@gmail.com';

function csvEscape(value) {
  const str = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function downloadCsv(rows) {
  const header = ['First Name', 'Email', 'Timeline', 'City', 'State', 'Min Bedrooms', 'Date Signed Up'];
  const lines = [header, ...rows.map(r => [
    r.first_name,
    r.email,
    r.timeline,
    r.city,
    r.state,
    r.min_bedrooms,
    new Date(r.created_at).toISOString(),
  ])];

  const csv = lines.map(line => line.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function AdminWaitlist() {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    fetchWaitlist();
  }, [isAdmin]);

  async function fetchWaitlist() {
    setLoading(true);
    setError('');
    const { data, error: fetchError } = await supabase
      .from('waitlist')
      .select('*')
      .order('created_at', { ascending: false });

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }

  if (authLoading) return (
    <div className="page" style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>
      loading...
    </div>
  );

  if (!user) return (
    <div className="page" style={{ textAlign: 'center', padding: 60 }}>
      <p style={{ color: 'var(--text-secondary)' }}>Please sign in to view this page.</p>
    </div>
  );

  if (!isAdmin) return (
    <div className="page" style={{ textAlign: 'center', padding: 60 }}>
      <p style={{ color: 'var(--text-secondary)' }}>You don't have access to this page.</p>
    </div>
  );

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600 }}>waitlist signups</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{rows.length} total</p>
        </div>
        <button className="btn btn-primary" onClick={() => downloadCsv(rows)} disabled={rows.length === 0}>
          export CSV
        </button>
      </div>

      {error && (
        <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', color: '#991B1B', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>loading signups...</div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>No waitlist signups yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                {['Name', 'Email', 'Timeline', 'City', 'Bedrooms', 'Date signed up'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', color: 'var(--text-secondary)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '10px 16px' }}>{r.first_name || '—'}</td>
                  <td style={{ padding: '10px 16px' }}>{r.email}</td>
                  <td style={{ padding: '10px 16px' }}>{r.timeline || '—'}</td>
                  <td style={{ padding: '10px 16px' }}>{[r.city, r.state].filter(Boolean).join(', ') || '—'}</td>
                  <td style={{ padding: '10px 16px' }}>{r.min_bedrooms ? `${r.min_bedrooms}+` : '—'}</td>
                  <td style={{ padding: '10px 16px' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
